import {
  Cell,
  Icon,
  SelectButton,
  SelectButtonGroup,
  SelectButtonGroupProps,
  useBeyondOnlineBreakpoints,
} from '@zeiss/beyond-online-react';
import { Box, Grid, RequiredField } from '@zeiss/lib-components';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { ApplicationSearch } from 'src/modules/Invitations/components/ApplicationSearch';
import { TenantSearch } from 'src/modules/Invitations/components/TenantSearch';
import { useWizardForm } from 'src/modules/Invitations/InvitationCreate/form';
import {
  getInvitationForFieldSchema,
  getInvitationTypeFieldSchema,
} from 'src/modules/Invitations/InvitationCreate/schema';
import type {
  InvitationTargetSelection,
  StepComponent,
} from 'src/modules/Invitations/InvitationCreate/types';
import { InvitationType } from 'src/types/rest/managementPortalApi';

type StepForFieldsProps = Parameters<StepComponent>[0];

const InvitationTypeSelector = ({
  columnCount,
}: {
  columnCount: SelectButtonGroupProps['columnCount'];
}) => {
  const { i18n, t } = useTranslation(['common', 'invitations']);
  const form = useWizardForm();

  const invitationTypeFieldSchema = useMemo(
    () => getInvitationTypeFieldSchema(t),
    [i18n.language, t],
  );

  return (
    <form.Field
      name="step1.invitationType"
      validators={{
        onChange: invitationTypeFieldSchema,
        onSubmit: invitationTypeFieldSchema,
      }}
    >
      {(field) => {
        const selectedType = field.state.value;

        const handleTypeChange = (
          event:
            | React.FormEvent<HTMLInputElement>
            | React.MouseEvent<HTMLButtonElement>,
        ) => {
          const nextType = event.currentTarget.value as InvitationType;

          field.handleChange(nextType);
          form.setFieldValue('step1.invitationFor', null);
        };

        return (
          <SelectButtonGroup
            activeColor="blue"
            columnCount={columnCount}
            name="invitation-type"
            onChange={handleTypeChange}
            value={selectedType ?? ''}
          >
            <SelectButton
              header={() => <Icon icon="NavigationApps" size={20} />}
              label={t('common:glossary.application')}
              value={InvitationType.application}
            />
            <SelectButton
              header={() => <Icon icon="Store" size={20} />}
              label={t('common:glossary.tenant')}
              value={InvitationType.tenant}
            />
          </SelectButtonGroup>
        );
      }}
    </form.Field>
  );
};

const InvitationTargetSelector = ({
  displayFieldError,
}: Pick<StepForFieldsProps, 'displayFieldError'>) => {
  const { t } = useTranslation(['common', 'invitations']);
  const form = useWizardForm();

  return (
    <form.Subscribe selector={(state) => state.values.step1.invitationType}>
      {(selectedType) => {
        if (!selectedType) {
          return null;
        }

        const targetFieldSchema = getInvitationForFieldSchema(t, selectedType);

        return (
          <RequiredField>
            <form.Field
              key={selectedType}
              name="step1.invitationFor"
              validators={{
                onChange: targetFieldSchema,
                onSubmit: targetFieldSchema,
              }}
            >
              {(field) => {
                const targetError = displayFieldError(field);
                const selectedTarget = field.state.value;
                const handleTargetChange = (
                  target: InvitationTargetSelection | null,
                ) => {
                  field.handleChange(target);
                };

                return selectedType === InvitationType.application ? (
                  <ApplicationSearch
                    error={targetError}
                    onSelectApplication={(application) => {
                      handleTargetChange(
                        application
                          ? {
                              id: application.id,
                              label: application.name,
                              redirectUrl: application.redirectUrl,
                            }
                          : null,
                      );
                    }}
                    selectedLabel={selectedTarget?.label}
                    selectedRedirectUrl={selectedTarget?.redirectUrl}
                  />
                ) : (
                  <TenantSearch
                    error={targetError}
                    onSelectTenant={(tenant) => {
                      handleTargetChange(
                        tenant
                          ? {
                              id: tenant.id,
                              label: tenant.name,
                              redirectUrl: tenant.redirectUrl,
                            }
                          : null,
                      );
                    }}
                    selectedLabel={selectedTarget?.label}
                    selectedRedirectUrl={selectedTarget?.redirectUrl}
                  />
                );
              }}
            </form.Field>
          </RequiredField>
        );
      }}
    </form.Subscribe>
  );
};

/**
 * TODO:
 * - target url needs to be added
 */
export const StepForFields: StepComponent = ({ displayFieldError }) => {
  const breakpoints = useBeyondOnlineBreakpoints();

  const columnCount: SelectButtonGroupProps['columnCount'] = breakpoints.m
    .screenIsSmallerThanThis
    ? [1, 2]
    : [2, 2];

  return (
    <Grid>
      <Cell.Basic colSpan={[4, 6, 8]}>
        <Box>
          <InvitationTypeSelector columnCount={columnCount} />
        </Box>
        <Box>
          <InvitationTargetSelector displayFieldError={displayFieldError} />
        </Box>
      </Cell.Basic>
    </Grid>
  );
};
